import { FC, Fragment } from 'react';
import FRMVisitReportWarehouseForm, {
	useWarehouseForm,
} from './VisitReportOfficePage.Warehouse.Form';
import {
	Button,
	Col,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	Table,
	Typography,
} from 'antd';
import { useMutation } from '@tanstack/react-query';
import { submitFRMOfficeObservationWarehouse } from '@/libs/fleet-api/services/visit-reports';
import { useFRMVisitReportPageContext } from './VisitReportOfficePage';
import FRMVisitReportSparePart from './VisitReportOfficePage.Warehouse.SparePart';
import FRMVisitReportInventory from './VisitReportOfficePage.Warehouse.Inventory';
import { useSelectMaterialOwnershipType } from '@/libs/fleet-api/hooks/master-data';
import { OfficeObservationWarehouse } from '@/libs/fleet-api/services/types';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import { OfficeMaterialOwnership } from '@/libs/fleet-api/enums';
import FRMVisitReportParetoSupplier from './VisitReportOfficePage.Warehouse.ParetoSupplier';
import FRMVisitReportParetoReceivable from './VisitReportOfficePage.Warehouse.Pareto';
import { inputCurrency } from '@/libs/antd/input-number';
import FleetTitle from '@/libs/antd/components/Title';
import { FormAlert, useFormAlert } from '@/libs/antd/form';

const FRMVisitReportOfficeWarehouse: FC<{ onSave: () => void }> = ({
	onSave,
}) => {
	const { surveyInformation, officeObservation, approvalHistories } =
		useFRMVisitReportPageContext();
	const { warehouse } = officeObservation ?? {};
	const warehouseForm = useWarehouseForm();
	const submitFRMOfficeObservationWarehouseMutation = useMutation({
		mutationFn: submitFRMOfficeObservationWarehouse,
	});

	const selectMaterialOwnershipType = useSelectMaterialOwnershipType();

	const { alerts, validate } = useFormAlert(warehouseForm.form, null);

	return (
		<Form.Provider
			onFormFinish={async (name, { values }) => {
				if (!name) return;
				await submitFRMOfficeObservationWarehouseMutation.mutateAsync({
					...(values as OfficeObservationWarehouse<'payload'>),
					surveyId: surveyInformation!.id!,
				});
				onSave?.();
			}}
		>
			<FRMVisitReportWarehouseForm {...warehouseForm} />
			<FRMVisitReportSparePart />
			<FRMVisitReportInventory />

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				name="materialOwnership"
				form={warehouseForm.form}
				labelCol={{
					span: 10,
				}}
				initialValues={
					{
						materialOwnershipTypeId: warehouse?.materialOwnershipType?.id,
						materialOwnershipPortion: warehouse?.materialOwnershipPortion,
					} as OfficeObservationWarehouse<'payload'>
				}
			>
				<Row gutter={[20, 40]}>
					<Col span={12}>
						<Form.Item
							name={warehouseForm.fieldName(['materialOwnershipTypeId'])}
							label="Kepemilikan Bahan"
							labelCol={{ span: 10 }}
							rules={[rule.required('Kepemilikan Bahan wajib diisi')]}
						>
							<Select
								{...selectMaterialOwnershipType}
								options={
									!warehouse?.materialOwnershipType ||
									selectMaterialOwnershipType?.options?.length
										? selectMaterialOwnershipType.options
										: [
												{
													label: warehouse.materialOwnershipType.value,
													value: warehouse.materialOwnershipType.id,
												},
											]
								}
							/>
						</Form.Item>
					</Col>
					{warehouseForm.value?.materialOwnershipTypeId ===
						OfficeMaterialOwnership.Konsinyasi && (
						<Col span={12}>
							<Form.Item
								name={warehouseForm.fieldName(['materialOwnershipPortion'])}
								label="Berapa % ?"
								labelCol={{ span: 10 }}
								rules={[rule.required('Berapa % wajib diisi')]}
							>
								<InputNumber min={0} />
							</Form.Item>
						</Col>
					)}
				</Row>
				<Divider />
			</Form>

			<FRMVisitReportParetoSupplier />

			<Typography.Title level={3}>List Piutang Pareto</Typography.Title>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={warehouseForm.form}
				name="warehouseNotes"
				initialValues={
					{
						totalReceivables: warehouse?.totalReceivables,
					} as OfficeObservationWarehouse<'payload'>
				}
			>
				<Row gutter={[20, 40]}>
					<Col span={12}>
						<Form.Item
							name={warehouseForm.fieldName('totalReceivables')}
							label="Total Piutang"
							labelCol={{ span: 10 }}
							rules={[rule.required('Total Piutang wajib diisi')]}
						>
							<InputNumber {...inputCurrency} />
						</Form.Item>
					</Col>
				</Row>
			</Form>
			<FRMVisitReportParetoReceivable />

			{approvalHistories?.length ? (
				<Fragment>
					<FleetTitle>Approval History</FleetTitle>
					<Table
						dataSource={
							approvalHistories?.map((record, key) => {
								return { ...record, key };
							}) ?? []
						}
						columns={[
							{
								title: 'No',
								width: 1,
								align: 'center',
								dataIndex: 'no',
							},
							{
								title: 'Name',
								dataIndex: 'approvalBy',
							},
							{
								title: 'Date',
								dataIndex: 'approvalDate',
							},
							{
								title: 'Process',
								dataIndex: 'approvalProcess',
							},
							{
								title: 'Status',
								align: 'center',
								render: (_, record) => {
									return (
										<span
											className={`review-status ${record.approvalStatus.toLowerCase()}`}
										>
											{record.approvalStatus}
										</span>
									);
								},
							},
							{
								title: 'Approval Notes',
								dataIndex: 'approvalNote',
							},
						]}
						bordered
						pagination={false}
						style={{ marginBottom: 20 }}
					/>
				</Fragment>
			) : null}

			<FormAlert alerts={alerts} />
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={warehouseForm.form}
				name="warehouseNotes"
				initialValues={
					{
						warehouseNotes: warehouse?.warehouseNotes,
					} as OfficeObservationWarehouse<'payload'>
				}
			>
				<Row gutter={[20, 40]}>
					<Col span={24}>
						<Form.Item
							name={warehouseForm.fieldName('warehouseNotes')}
							label="Informasi Tambahan"
							labelCol={{
								span: 5,
								style: {
									alignSelf: 'center',
								},
							}}
							wrapperCol={{
								style: {
									marginLeft: -5,
								},
							}}
							rules={[rule.required('Informasi Tambahan wajib diisi')]}
						>
							<Input.TextArea rows={4} />
						</Form.Item>
					</Col>
				</Row>
			</Form>

			<Button
				type="primary"
				style={{
					width: 200,
					display: 'block',
					marginLeft: 'auto',
					marginTop: 30,
				}}
				loading={submitFRMOfficeObservationWarehouseMutation.isPending}
				onClick={() => {
					validate();
					warehouseForm.form.submit();
				}}
			>
				SAVE
			</Button>
		</Form.Provider>
	);
};

export default FRMVisitReportOfficeWarehouse;
